import { useCallback, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ConciergeDevOptions, RankingWeights } from '../types/devOptions';
import { fetchDevOptions } from '../api';

export interface UseDevOptionsReturn {
  /** Whether dev mode is active (via ?isDev=true URL param) */
  isDevMode: boolean;
  /** True until the server-side defaults have loaded from /concierge/devOptions */
  isLoading: boolean;
  /** Whether the dev options panel is expanded */
  devOptionsExpanded: boolean;
  setDevOptionsExpanded: React.Dispatch<React.SetStateAction<boolean>>;
  /** Individual state values */
  useTextRecall: boolean;
  setUseTextRecall: React.Dispatch<React.SetStateAction<boolean>>;
  pageSize: number;
  setPageSize: React.Dispatch<React.SetStateAction<number>>;
  rankingWeights: RankingWeights;
  setRankingWeights: React.Dispatch<React.SetStateAction<RankingWeights>>;
  /** Adjusts a single weight and auto-normalizes the others so total stays at 1.0 */
  handleWeightChange: (key: keyof RankingWeights, newValue: number) => void;
  /** When true, uses targeted LLM call for section titles instead of letting the main LLM categorize */
  useTargetedSectionTitles: boolean;
  setUseTargetedSectionTitles: React.Dispatch<React.SetStateAction<boolean>>;
  /** When true, uses two-phase filtered KNN (text recall → cosine re-rank) instead of KNN with post_filter */
  useTwoPhaseFiltering: boolean;
  setUseTwoPhaseFiltering: React.Dispatch<React.SetStateAction<boolean>>;
  /** When true, runs LLM outlier filtering before recategorization */
  useOutlierFiltering: boolean;
  setUseOutlierFiltering: React.Dispatch<React.SetStateAction<boolean>>;
  /** When true, skips rating-based pre-filter so all rated products reach the LLM */
  ignoreRatings: boolean;
  setIgnoreRatings: React.Dispatch<React.SetStateAction<boolean>>;
  /** Minimum rating for pre-filter (only when ignoreRatings is false) */
  ratingThreshold: number;
  setRatingThreshold: React.Dispatch<React.SetStateAction<number>>;
  /** LLM model for heavy calls */
  llmModel: string;
  setLlmModel: React.Dispatch<React.SetStateAction<string>>;
  /** LLM model for lightweight calls */
  llmModelLight: string;
  setLlmModelLight: React.Dispatch<React.SetStateAction<string>>;
  /** Temperature for search angle generation */
  searchTemperature: number;
  setSearchTemperature: React.Dispatch<React.SetStateAction<number>>;
  /** Max products per brand in diversity selection */
  maxPerBrand: number;
  setMaxPerBrand: React.Dispatch<React.SetStateAction<number>>;
  /** Max total products sent to recategorization LLM */
  maxProductsForLLM: number;
  setMaxProductsForLLM: React.Dispatch<React.SetStateAction<number>>;
  /** Number of parallel search angles */
  numSearchAngles: number;
  setNumSearchAngles: React.Dispatch<React.SetStateAction<number>>;
  /** Target number of thematic categories */
  targetCategories: number;
  setTargetCategories: React.Dispatch<React.SetStateAction<number>>;
  /** Minimum semantic similarity threshold */
  semanticThreshold: number;
  setSemanticThreshold: React.Dispatch<React.SetStateAction<number>>;
  /** Products per section in final results */
  productsPerSection: number;
  setProductsPerSection: React.Dispatch<React.SetStateAction<number>>;
  /** Extra products fetched per section before filtering */
  productsBuffer: number;
  setProductsBuffer: React.Dispatch<React.SetStateAction<number>>;
  /** Controls which angle generation pipeline to use */
  angleStrategy: 'brand' | 'generic' | 'auto';
  setAngleStrategy: React.Dispatch<React.SetStateAction<'brand' | 'generic' | 'auto'>>;
  /** Controls the isAvailable ES pre-filter */
  availabilityFilter: 'apply' | 'skipFalse' | 'skipAll';
  setAvailabilityFilter: React.Dispatch<React.SetStateAction<'apply' | 'skipFalse' | 'skipAll'>>;
  /** When true, re-ranks products using original query embedding */
  useOriginalQueryReranking: boolean;
  setUseOriginalQueryReranking: React.Dispatch<React.SetStateAction<boolean>>;
  /** LLM model for recategorization step (empty = use llmModel) */
  recatModel: string;
  setRecatModel: React.Dispatch<React.SetStateAction<string>>;
  /** LLM model for the query analysis call (empty = use llmModelLight) */
  analyzeModel: string;
  setAnalyzeModel: React.Dispatch<React.SetStateAction<string>>;
  /** LLM model for the search planning call (empty = use llmModelLight) */
  planModel: string;
  setPlanModel: React.Dispatch<React.SetStateAction<string>>;
  /** Langfuse prompt label (empty = 'production') */
  promptLabel: string;
  setPromptLabel: React.Dispatch<React.SetStateAction<string>>;
  /** Fully assembled devOptions object ready to pass to useStreamingSearch.
   *  undefined when dev mode is inactive so the server-side defaults are used. */
  devOptions: ConciergeDevOptions | undefined;
}

export const useDevOptions = (): UseDevOptionsReturn => {
  const isDevMode = useMemo(
    () => new URLSearchParams(window.location.search).get('isDev') === 'true',
    [],
  );

  const urlLabel = useMemo(
    () => new URLSearchParams(window.location.search).get('label') || '',
    [],
  );

  // Server-side defaults loaded from DynamoDB via GET /concierge/devOptions.
  // Using React Query so multiple hook instances share a single in-flight request.
  const { data: serverDefaults = null, isLoading } = useQuery<ConciergeDevOptions | null>({
    queryKey: ['concierge-dev-options'],
    queryFn: () => fetchDevOptions(),
    staleTime: 5 * 60 * 1000,
  });

  const [devOptionsExpanded, setDevOptionsExpanded] = useState(false);

  // Individual overrides — initialized to null, meaning "use server default".
  // Once a user touches a control in the dev panel, the override takes effect.
  const [useTextRecallOverride, setUseTextRecall] = useState<boolean | null>(null);
  const [pageSizeOverride, setPageSize] = useState<number | null>(null);
  const [rankingWeightsOverride, setRankingWeights] = useState<RankingWeights | null>(null);
  const [useTargetedSectionTitlesOverride, setUseTargetedSectionTitles] = useState<boolean | null>(null);
  const [useTwoPhaseFilteringOverride, setUseTwoPhaseFiltering] = useState<boolean | null>(null);
  const [useOutlierFilteringOverride, setUseOutlierFiltering] = useState<boolean | null>(null);
  const [ignoreRatingsOverride, setIgnoreRatings] = useState<boolean | null>(null);
  const [ratingThresholdOverride, setRatingThreshold] = useState<number | null>(null);
  const [llmModelOverride, setLlmModel] = useState<string | null>(null);
  const [llmModelLightOverride, setLlmModelLight] = useState<string | null>(null);
  const [searchTemperatureOverride, setSearchTemperature] = useState<number | null>(null);
  const [maxPerBrandOverride, setMaxPerBrand] = useState<number | null>(null);
  const [maxProductsForLLMOverride, setMaxProductsForLLM] = useState<number | null>(null);
  const [numSearchAnglesOverride, setNumSearchAngles] = useState<number | null>(null);
  const [targetCategoriesOverride, setTargetCategories] = useState<number | null>(null);
  const [semanticThresholdOverride, setSemanticThreshold] = useState<number | null>(null);
  const [productsPerSectionOverride, setProductsPerSection] = useState<number | null>(null);
  const [productsBufferOverride, setProductsBuffer] = useState<number | null>(null);
  const [angleStrategyOverride, setAngleStrategy] = useState<'brand' | 'generic' | 'auto' | null>(null);
  const [availabilityFilterOverride, setAvailabilityFilter] = useState<'apply' | 'skipFalse' | 'skipAll' | null>(null);
  const [useOriginalQueryRerankingOverride, setUseOriginalQueryReranking] = useState<boolean | null>(null);
  const [recatModelOverride, setRecatModel] = useState<string | null>(null);
  const [analyzeModelOverride, setAnalyzeModel] = useState<string | null>(null);
  const [planModelOverride, setPlanModel] = useState<string | null>(null);
  const [promptLabelOverride, setPromptLabel] = useState<string | null>(null);

  // Resolved values: override ?? server default (safe defaults while loading)
  const useTextRecall = useTextRecallOverride ?? serverDefaults?.useTextRecall ?? false;
  const pageSize = pageSizeOverride ?? serverDefaults?.pageSize ?? 20;
  const rankingWeights = rankingWeightsOverride ?? serverDefaults?.rankingWeights ?? { semanticWeight: 0.4, salesWeight: 0.15, contentWeight: 0.1, reviewWeight: 0.15, rewardWeight: 0.1, priceWeight: 0.1 };
  const useTargetedSectionTitles = useTargetedSectionTitlesOverride ?? serverDefaults?.useTargetedSectionTitles ?? true;
  const useTwoPhaseFiltering = useTwoPhaseFilteringOverride ?? serverDefaults?.useTwoPhaseFiltering ?? true;
  const useOutlierFiltering = useOutlierFilteringOverride ?? serverDefaults?.useOutlierFiltering ?? true;
  const ignoreRatings = ignoreRatingsOverride ?? serverDefaults?.ignoreRatings ?? true;
  const ratingThreshold = ratingThresholdOverride ?? serverDefaults?.ratingThreshold ?? 2.0;
  const llmModel = llmModelOverride ?? serverDefaults?.llmModel ?? 'gpt-4o';
  const llmModelLight = llmModelLightOverride ?? serverDefaults?.llmModelLight ?? 'gpt-4o-mini';
  const searchTemperature = searchTemperatureOverride ?? serverDefaults?.searchTemperature ?? 0.5;
  const maxPerBrand = maxPerBrandOverride ?? serverDefaults?.maxPerBrand ?? 3;
  const maxProductsForLLM = maxProductsForLLMOverride ?? serverDefaults?.maxProductsForLLM ?? 50;
  const numSearchAngles = numSearchAnglesOverride ?? serverDefaults?.numSearchAngles ?? 6;
  const targetCategories = targetCategoriesOverride ?? serverDefaults?.targetCategories ?? 4;
  const semanticThreshold = semanticThresholdOverride ?? serverDefaults?.semanticThreshold ?? 0.65;
  const productsPerSection = productsPerSectionOverride ?? serverDefaults?.productsPerSection ?? 10;
  const productsBuffer = productsBufferOverride ?? serverDefaults?.productsBuffer ?? 15;
  const angleStrategy = angleStrategyOverride ?? serverDefaults?.angleStrategy ?? 'auto';
  const availabilityFilter = availabilityFilterOverride ?? serverDefaults?.availabilityFilter ?? 'apply';
  const useOriginalQueryReranking = useOriginalQueryRerankingOverride ?? serverDefaults?.useOriginalQueryReranking ?? true;
  const recatModel = recatModelOverride ?? serverDefaults?.recatModel ?? '';
  const analyzeModel = analyzeModelOverride ?? serverDefaults?.analyzeModel ?? '';
  const planModel = planModelOverride ?? serverDefaults?.planModel ?? '';
  const promptLabel = promptLabelOverride ?? (urlLabel || serverDefaults?.promptLabel) ?? '';

  const handleWeightChange = useCallback((key: keyof RankingWeights, newValue: number) => {
    setRankingWeights((prev) => {
      const current = prev ?? rankingWeights;
      const otherKeys = (Object.keys(current) as Array<keyof RankingWeights>).filter((k) => k !== key);
      const otherSum = otherKeys.reduce((sum, k) => sum + current[k], 0);
      const remaining = Math.max(0, 1 - newValue);
      const updated = { ...current, [key]: newValue };
      otherKeys.forEach((k) => {
        updated[k] = otherSum > 0 ? (current[k] / otherSum) * remaining : remaining / otherKeys.length;
      });
      return updated;
    });
  }, [rankingWeights]);

  const devOptions: ConciergeDevOptions | undefined = useMemo(
    () => {
      if (!isDevMode && !urlLabel) return undefined;
      return {
        useTextRecall,
        pageSize,
        rankingWeights,
        useTargetedSectionTitles,
        useTwoPhaseFiltering,
        useOutlierFiltering,
        ignoreRatings,
        ratingThreshold,
        llmModel,
        llmModelLight,
        searchTemperature,
        maxPerBrand,
        maxProductsForLLM,
        numSearchAngles,
        targetCategories,
        semanticThreshold,
        productsPerSection,
        productsBuffer,
        angleStrategy,
        availabilityFilter,
        useOriginalQueryReranking,
        recatModel,
        analyzeModel,
        planModel,
        promptLabel,
      };
    },
    [
      isDevMode,
      urlLabel,
      useTextRecall,
      pageSize,
      rankingWeights,
      useTargetedSectionTitles,
      useTwoPhaseFiltering,
      useOutlierFiltering,
      ignoreRatings,
      ratingThreshold,
      llmModel,
      llmModelLight,
      searchTemperature,
      maxPerBrand,
      maxProductsForLLM,
      numSearchAngles,
      targetCategories,
      semanticThreshold,
      productsPerSection,
      productsBuffer,
      angleStrategy,
      availabilityFilter,
      useOriginalQueryReranking,
      recatModel,
      analyzeModel,
      planModel,
      promptLabel,
    ],
  );

  return {
    isDevMode,
    isLoading,
    devOptionsExpanded,
    setDevOptionsExpanded,
    useTextRecall,
    setUseTextRecall: setUseTextRecall as React.Dispatch<React.SetStateAction<boolean>>,
    pageSize,
    setPageSize: setPageSize as React.Dispatch<React.SetStateAction<number>>,
    rankingWeights,
    setRankingWeights: setRankingWeights as React.Dispatch<React.SetStateAction<RankingWeights>>,
    handleWeightChange,
    useTargetedSectionTitles,
    setUseTargetedSectionTitles: setUseTargetedSectionTitles as React.Dispatch<React.SetStateAction<boolean>>,
    useTwoPhaseFiltering,
    setUseTwoPhaseFiltering: setUseTwoPhaseFiltering as React.Dispatch<React.SetStateAction<boolean>>,
    useOutlierFiltering,
    setUseOutlierFiltering: setUseOutlierFiltering as React.Dispatch<React.SetStateAction<boolean>>,
    ignoreRatings,
    setIgnoreRatings: setIgnoreRatings as React.Dispatch<React.SetStateAction<boolean>>,
    ratingThreshold,
    setRatingThreshold: setRatingThreshold as React.Dispatch<React.SetStateAction<number>>,
    llmModel,
    setLlmModel: setLlmModel as React.Dispatch<React.SetStateAction<string>>,
    llmModelLight,
    setLlmModelLight: setLlmModelLight as React.Dispatch<React.SetStateAction<string>>,
    searchTemperature,
    setSearchTemperature: setSearchTemperature as React.Dispatch<React.SetStateAction<number>>,
    maxPerBrand,
    setMaxPerBrand: setMaxPerBrand as React.Dispatch<React.SetStateAction<number>>,
    maxProductsForLLM,
    setMaxProductsForLLM: setMaxProductsForLLM as React.Dispatch<React.SetStateAction<number>>,
    numSearchAngles,
    setNumSearchAngles: setNumSearchAngles as React.Dispatch<React.SetStateAction<number>>,
    targetCategories,
    setTargetCategories: setTargetCategories as React.Dispatch<React.SetStateAction<number>>,
    semanticThreshold,
    setSemanticThreshold: setSemanticThreshold as React.Dispatch<React.SetStateAction<number>>,
    productsPerSection,
    setProductsPerSection: setProductsPerSection as React.Dispatch<React.SetStateAction<number>>,
    productsBuffer,
    setProductsBuffer: setProductsBuffer as React.Dispatch<React.SetStateAction<number>>,
    angleStrategy,
    setAngleStrategy: setAngleStrategy as React.Dispatch<React.SetStateAction<'brand' | 'generic' | 'auto'>>,
    availabilityFilter,
    setAvailabilityFilter: setAvailabilityFilter as React.Dispatch<React.SetStateAction<'apply' | 'skipFalse' | 'skipAll'>>,
    useOriginalQueryReranking,
    setUseOriginalQueryReranking: setUseOriginalQueryReranking as React.Dispatch<React.SetStateAction<boolean>>,
    recatModel,
    setRecatModel: setRecatModel as React.Dispatch<React.SetStateAction<string>>,
    analyzeModel,
    setAnalyzeModel: setAnalyzeModel as React.Dispatch<React.SetStateAction<string>>,
    planModel,
    setPlanModel: setPlanModel as React.Dispatch<React.SetStateAction<string>>,
    promptLabel,
    setPromptLabel: setPromptLabel as React.Dispatch<React.SetStateAction<string>>,
    devOptions,
  };
};
